import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, localISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, LogIn, LogOut, Loader2, QrCode, CalendarOff, MapPin } from "lucide-react";
import { fmtTime } from "@/lib/format";

export default function ClockWidget({ onChange, locationOnly = false }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const [a, m] = await Promise.allSettled([api.get("/attendance/active"), api.get("/attendance/mode")]);
      if (a.status === "fulfilled") setActive(a.value.data.active);
      if (m.status === "fulfilled") setMode(m.value.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // Keep clock status fresh so a manager's early-clock-out approval/rejection reflects without a manual refresh.
  useEffect(() => {
    const t = setInterval(() => { load(); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clockIn = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/clock-in", { timestamp: localISO(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      toast.success("Clocked in. Have a great shift!");
      await load(); onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  const locationCheckIn = async () => {
    setBusy(true);
    try {
      const loc = await getLocation();
      if (loc.lat == null) { toast.error("Couldn't get your location. Please allow location access and try again."); return; }
      const { data } = await api.post("/attendance/checkin-location", {
        timestamp: localISO(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, lat: loc.lat, lng: loc.lng,
      });
      toast.success(`Checked in at ${data.clock_in_workplace || "your workplace"}.`);
      await load(); onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const checkoutLocation = async () => {
    setBusy(true);
    try {
      const loc = await getLocation();
      if (loc.lat == null) { toast.error("Couldn't get your location. Please allow location access and try again."); return; }
      const { data } = await api.post("/attendance/checkout-location", {
        timestamp: localISO(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, lat: loc.lat, lng: loc.lng,
      });
      toast.success(`Clocked out at ${data.clock_out_workplace || "your workplace"}.`);
      await load(); onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clockOut = async (withReason) => {
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/clock-out", { timestamp: localISO(), reason: withReason ? reason : undefined });
      if (data && data.clock_out == null && data.early_out_request?.status === "pending") {
        toast.success("Early clock-out submitted. You stay checked in until a manager approves.");
      } else {
        toast.success("Clocked out. See you next time!");
      }
      setReasonOpen(false); setReason("");
      await load(); onChange?.();
    } catch (err) {
      if (err.response?.status === 422) {
        setReasonOpen(true);
        toast.warning(apiError(err.response?.data?.detail));
      } else {
        toast.error(apiError(err.response?.data?.detail));
      }
    } finally { setBusy(false); }
  };

  const clockedIn = !!active;
  const pendingEarly = active?.early_out_request?.status === "pending";
  const eff = mode?.effective;

  const renderAction = () => {
    if (loading) return <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />;
    if (locationOnly) {
      if (clockedIn) {
        return (
          <Button onClick={checkoutLocation} disabled={busy} variant="outline" className="w-full border-emerald-500/40 hover:bg-emerald-500/10" data-testid="location-checkout-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4 mr-2" /> Clock out with location</>}
          </Button>
        );
      }
      return (
        <div className="w-full space-y-2" data-testid="manager-location-clock">
          <p className="text-center text-xs text-muted-foreground mb-1">You clock in and out using your location at a workplace.</p>
          <Button onClick={locationCheckIn} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="location-checkin-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4 mr-2" /> Clock in with location</>}
          </Button>
        </div>
      );
    }
    if (clockedIn) {
      if (pendingEarly) {
        return (
          <div className="w-full text-center text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg py-3 px-3" data-testid="early-out-pending">
            Early clock-out awaiting manager approval. You remain checked in.
          </div>
        );
      }
      return (
        <Button onClick={() => clockOut(false)} disabled={busy} variant="outline" className="w-full border-emerald-500/40 hover:bg-emerald-500/10" data-testid="clock-out-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4 mr-2" /> Clock out</>}
        </Button>
      );
    }
    // not clocked in — behaviour depends on today's effective work mode
    if (eff === "onsite") {
      return (
        <div className="w-full space-y-2" data-testid="onsite-qr-required">
          <p className="text-center text-sm text-sky-200/90 mb-1">On-site check-in — scan the workplace QR, or check in with your location if the QR isn't available.</p>
          <Button onClick={() => navigate("/scan")} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="scan-qr-btn">
            <QrCode className="w-4 h-4 mr-2" /> Scan QR to clock in
          </Button>
          <Button onClick={locationCheckIn} disabled={busy} variant="outline" className="w-full border-emerald-500/40 hover:bg-emerald-500/10" data-testid="location-checkin-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4 mr-2" /> Check in with location</>}
          </Button>
        </div>
      );
    }
    if (eff === "off") {
      return (
        <div className="w-full text-center text-sm text-muted-foreground bg-white/5 rounded-lg py-4 px-3" data-testid="no-shift-today">
          <CalendarOff className="w-6 h-6 mx-auto mb-1.5" />
          No scheduled shift today on your {mode?.work_mode} schedule.
        </div>
      );
    }
    // remote / hybrid-remote day
    return (
      <Button onClick={clockIn} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="clock-in-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Clock in (remote)</>}
      </Button>
    );
  };

  return (
    <div className="card-emerald p-6 sm:p-8 w-full min-w-0 overflow-hidden" data-testid="clock-in-out-widget">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Time clock</span>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${clockedIn ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-muted-foreground"}`}>
          <span className={`w-2 h-2 rounded-full ${clockedIn ? "bg-emerald-400 pulse-dot" : "bg-muted-foreground"}`} />
          {clockedIn ? "Clocked in" : "Clocked out"}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Clock className="w-6 h-6 text-emerald-400" />
        <span className="font-mono text-4xl sm:text-5xl font-bold tracking-tight" data-testid="live-clock">
          {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-1">{now.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>

      {mode && !clockedIn && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="workmode-label">
          Work mode: <span className="text-foreground font-medium capitalize">{mode.work_mode}</span>
          {mode.work_mode !== "onsite" && <> · today is <span className="text-foreground font-medium">{eff === "off" ? "not scheduled" : eff}</span></>}
        </p>
      )}

      {clockedIn && (
        <p className="mt-4 text-sm text-muted-foreground">
          Started at <span className="text-foreground font-medium">{fmtTime(active.clock_in)}</span>
          {" · "}Set clock-out <span className="text-foreground font-medium">{active.expected_clock_out}</span>
        </p>
      )}

      <div className="mt-6">{renderAction()}</div>

      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent className="bg-[#121816] border-emerald-500/20">
          <DialogHeader>
            <DialogTitle>Reason required</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Your clock-out deviates from the set time. Provide a reason for manager approval.
          </p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="e.g. Stayed late to finish the shipment report" className="bg-[#0A0D0C] border-emerald-500/20" data-testid="clock-out-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonOpen(false)}>Cancel</Button>
            <Button onClick={() => clockOut(true)} disabled={busy || !reason.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="clock-out-reason-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
